<template>
  <b-container
    tag="section"
    class="analytics">
    <b-row>
      <b-col md="12">
        <h2 class="dashboard__title">
          Analytics
        </h2>
      </b-col>
      <b-col
        md="12"
        class="mb-4">
        <DatePicker
          ref="dateRange"
          :omit-shortcuts="['today', 'yesterday', 'all']"
          default-date="last7Days"
          class="date-range-select"
          @datesChanged="changeDateRange"/>
        <b-dropdown
          :clearable="false"
          right
          class="date-point-select">
          <template slot="button-content">
            {{ datePoints[selectedDatePoint] }}
          </template>
          <b-dropdown-item
            v-for="(pointName, point) in datePoints"
            :key="point"
            @click="selectDatePoint(point)">{{ pointName }}</b-dropdown-item>
        </b-dropdown>
      </b-col>
      <template v-if="analyticsLoaded">
        <b-col
          md="12"
          class="mb-4">
          <b-row>
            <b-col
              md="3"
              sm="6">
              <div class="analytics-card">
                <h3 class="main-info">{{ analytics.contacts.total }}</h3>
                <h4>Contacts</h4>
              </div>
            </b-col>
            <b-col
              md="3"
              sm="6">
              <div class="analytics-card">
                <h3 class="main-info">{{ analytics.subscribers.total }}</h3>
                <h4>Subscribers</h4>
              </div>
            </b-col>
            <b-col
              md="3"
              sm="6">
              <div class="analytics-card">
                <h3 class="main-info">{{ analytics.sent_template_messages.total }}</h3>
                <h4>Outbound Notifications</h4>
              </div>
            </b-col>
            <b-col
              md="3"
              sm="6">
              <div class="analytics-card">
                <h3 class="main-info">{{ analytics.messages.total_inbound }}</h3>
                <h4>Inbound Messages</h4>
              </div>
            </b-col>
            <b-col
              md="3"
              sm="6">
              <div class="analytics-card">
                <h3 class="main-info">{{ analytics.messages.total_outbound }}</h3>
                <h4>Outbound Messages</h4>
              </div>
            </b-col>
            <b-col
              md="3"
              sm="6">
              <div class="analytics-card">
                <h3 class="main-info">{{ analytics.messages.total_conversations }}</h3>
                <h4>Conversations</h4>
              </div>
            </b-col>
            <b-col
              md="3"
              sm="6">
              <div class="analytics-card">
                <h3 class="main-info">{{ averageResolutionTime }}</h3>
                <h4>Average Resolution Time</h4>
              </div>
            </b-col>
            <b-col
              md="3"
              sm="6">
              <div class="analytics-card">
                <h3 class="main-info">{{ averageFirstReplyTime }}</h3>
                <h4>Average First Reply Time</h4>
              </div>
            </b-col>
          </b-row>
        </b-col>
        <b-col md="12">
          <div class="analytics-section">
            <div class="analytics-header">
              <h4>Contacts</h4>
            </div>
            <div class="analytics-body">
              <LineChart
                :styles="{'max-height' : '350px'}"
                :options="chartOptions"
                :chart-data="contactsChartData"
                chart-id="contacts-chart"/>
            </div>
          </div>
        </b-col>
        <b-col md="12">
          <div class="analytics-section">
            <div class="analytics-header">
              <h4>Subscribers</h4>
            </div>
            <div class="analytics-body">
              <LineChart
                :styles="{'max-height' : '350px'}"
                :options="chartOptions"
                :chart-data="subscribersChartData"
                chart-id="subscribers-chart"/>
            </div>
          </div>
        </b-col>
        <b-col md="12">
          <div class="analytics-section">
            <div class="analytics-header">
              <h4>Messages</h4>
            </div>
            <div class="analytics-body">
              <LineChart
                :styles="{'max-height' : '350px'}"
                :options="chartOptions"
                :chart-data="messagesChartData"
                chart-id="messages-chart"/>
              <div class="mt-5">
                <h5>Average First Reply Time</h5>
                <BarChart
                  :styles="{'height' : '300px'}"
                  :options="percentChartOptions"
                  :chart-data="firstRepliesChartData"
                  chart-id="first-reply-time-chart"/>
              </div>
            </div>
          </div>
        </b-col>
        <b-col md="12">
          <div class="analytics-section">
            <div class="analytics-header">
              <h4>Notifications</h4>
            </div>
            <div class="analytics-body">
              <LineChart
                :styles="{'max-height' : '350px'}"
                :options="chartOptions"
                :chart-data="notificationsChartData"
                chart-id="notifications-chart"/>
              <div class="mt-5">
                <h5>Templates</h5>
                <TemplateMessagesTable
                  :dates="dates"
                  :template-data="analytics.template_messages"
                  :chart-labels="chartLabels"/>
              </div>
            </div>
          </div>
        </b-col>
        <b-col
          md="12"
          class="mt-3 mb-5">
          <div class="analytics-section">
            <div class="analytics-header">
              <h4>Automation</h4>
            </div>
            <div class="analytics-body">
              <LineChart
                :styles="{'max-height' : '350px'}"
                :options="chartOptions"
                :chart-data="automationChartData"
                chart-id="automation-chart"/>
              <div class="mt-5">
                <h5>Keywords</h5>
                <AutomationKeywordsTable
                  :dates="dates"
                  :keyword-data="analytics.keywords"
                  :chart-labels="chartLabels"/>
              </div>
            </div>
          </div>
        </b-col>
      </template>
      <b-col
        v-else
        md="12">
        <div class="card-loading">
          <div
            class="loader loader-main">
            <div class="loading-circle"/>
          </div>
        </div>
      </b-col>
      <!--<b-col
        md="12"
        class="mt-3 mb-3">
        <div class="analytics-section">
          <div class="analytics-header">
            <h4>Payments</h4>
          </div>
          <div class="analytics-body">
            <LineChart
              :styles="{'max-height' : '350px'}"
              :options="chartOptions"
              :chart-data="paymentsChartData"
              chart-id="payments-chart"/>
          </div>
        </div>
      </b-col>-->
    </b-row>
  </b-container>
</template>

<script>
import '@/assets/css/modules/_analytics.scss';

import { mapGetters, mapActions } from 'vuex';
import { duration } from 'moment';
import LineChart from '../../common/charts/Line';
import BarChart from '../../common/charts/Bar';
import DatePicker from '../../common/DatePicker';
import TemplateMessagesTable from './TemplateMessagesTable';
import AutomationKeywordsTable from './AutomationKeywordsTable';

export default {
  name: 'Analytics',
  components: {
    DatePicker,
    LineChart,
    BarChart,
    TemplateMessagesTable,
    AutomationKeywordsTable,
  },
  data() {
    return {
      analyticsLoaded: false,
      selectedDatePoint: 'day',
      contactsChartData: null,
      subscribersChartData: null,
      messagesChartData: null,
      notificationsChartData: null,
      automationChartData: null,
      paymentsChartData: null,
      startDate: null,
      endDate: null,
      chartOptions: {
        maintainAspectRatio: false,
        legend: {
          labels: {
            usePointStyle: true,
            fontColor: '#253756',
          },
        },
        tooltips: {
          mode: 'index',
          intersect: false,
        },
        hover: {
          mode: 'nearest',
          intersect: true,
        },
        scales: {
          // xAxes: [{
          // }],
          yAxes: [{
            ticks: {
              beginAtZero: true,
              precision: 0,
            },
          }],
        },
      },
      percentChartOptions: {
        maintainAspectRatio: false,
        legend: false,
        tooltips: {
          mode: 'index',
          intersect: false,
          callbacks: {
            label: tooltipItems => `${tooltipItems.yLabel}%`,
          },
        },
        hover: {
          mode: 'nearest',
          intersect: true,
        },
        scales: {
          // xAxes: [{
          // }],
          yAxes: [{
            ticks: {
              min: 0,
              max: 100,
              callback: value => `${value}%`,
            },
          }],
        },
      },
    };
  },
  computed: {
    ...mapGetters({
      analytics: 'merchant/analytics',
    }),
    datePoints() {
      // if range is more than 31 days, then remove daily point
      if (!this.startDate || ((this.endDate - this.startDate) <= 2678400)) {
        return {
          day: 'Daily',
          week: 'Weekly',
          month: 'Monthly',
        };
      }

      return {
        week: 'Weekly',
        month: 'Monthly',
      };
    },
    dateFormat() {
      return (this.selectedDatePoint === 'month') ? 'MMM' : 'MMM Do';
    },
    chartLabels() {
      return this.analytics.date_points.map((interval) => {
        switch (this.selectedDatePoint) {
          case 'month': {
            return this.$moment(false, false).unix(interval).format('MMM');
          }
          case 'week': {
            const start = this.$moment(false, false).unix(interval).format('MMM DD');
            const end = this.$moment(false, false).unix(interval).add(6, 'days').format('MMM DD');
            return `${start} - ${end}`;
          }
          case 'day':
          default: {
            return this.$moment(false, false).unix(interval).format('MMM Do');
          }
        }
      });
    },
    dates() {
      return {
        time_zone: this.$moment(null, false).tz.guess(),
        date_point: this.selectedDatePoint,
        start_date: this.startDate,
        end_date: this.endDate,
      };
    },
    averageResolutionTime() {
      return this.durationToString(this.analytics.message_responses.average_resolution_time) || 0;
    },
    averageFirstReplyTime() {
      return this.durationToString(this.analytics.message_responses.average_first_reply_time) || 0;
    },
  },
  watch: {
    datePoints(datePoints) {
      if (Object.keys(datePoints).length === 2) {
        this.selectedDatePoint = 'week';
      } else {
        this.selectedDatePoint = 'day';
      }
    },
    analytics() {
      this.fillChartData();
      this.analyticsLoaded = true;
    },
  },
  methods: {
    ...mapActions({
      fetchAnalytics: 'merchant/fetchAnalytics',
    }),
    getAnalytics() {
      this.analyticsLoaded = false;
      const payload = {
        merchant_id: this.$route.params.merchant_id,
        data: this.dates,
      };

      this.fetchAnalytics(payload);
    },
    selectDatePoint(point) {
      this.selectedDatePoint = point;
      this.$nextTick(() => {
        this.getAnalytics();
      });
    },
    changeDateRange(dates) {
      this.startDate = dates[0].unix();
      this.endDate = dates[1].unix();
      this.$nextTick(() => {
        this.getAnalytics();
      });
    },
    fillChartData() {
      this.contactsChartData = {
        labels: this.chartLabels,
        datasets: [
          {
            label: 'All Contacts',
            backgroundColor: 'rgba(61, 139, 252, 0.1)',
            borderColor: 'rgb(61, 139, 252)',
            data: Object.values(this.analytics.contacts.all),
          },
          {
            label: 'New Contacts',
            backgroundColor: 'rgba(121, 113, 234, 0.1)',
            borderColor: 'rgb(121, 113, 234)',
            data: Object.values(this.analytics.contacts.new),
          },
          {
            label: 'Active Contacts',
            backgroundColor: 'rgba(0, 191, 165, 0.1)',
            borderColor: 'rgb(0, 191, 165)',
            data: Object.values(this.analytics.contact_activities.daily_contacted),
          },
        ],
      };

      this.subscribersChartData = {
        labels: this.chartLabels,
        datasets: [
          {
            label: 'All Subscribers',
            backgroundColor: 'rgba(61, 139, 252, 0.1)',
            borderColor: 'rgb(61, 139, 252)',
            data: Object.values(this.analytics.subscribers.all),
          },
          {
            label: 'New Subscribers',
            backgroundColor: 'rgba(0, 191, 165, 0.1)',
            borderColor: 'rgb(0, 191, 165)',
            data: Object.values(this.analytics.subscribers.subscribed),
          },
          {
            label: 'Unsubscribed',
            backgroundColor: 'rgb(237, 116, 116, 0.1)',
            borderColor: 'rgb(237, 116, 116)',
            data: Object.values(this.analytics.subscribers.unsubscribed),
          },
        ],
      };

      this.messagesChartData = {
        labels: this.chartLabels,
        datasets: [
          {
            label: 'Outbound Messages',
            backgroundColor: 'rgba(61, 139, 252, 0.1)',
            borderColor: 'rgb(61, 139, 252)',
            data: Object.values(this.analytics.messages.outbound),
          },
          {
            label: 'Inbound Messages',
            backgroundColor: 'rgba(0, 191, 165, 0.1)',
            borderColor: 'rgb(0, 191, 165)',
            data: Object.values(this.analytics.messages.inbound),
          },
          {
            label: 'Conversations',
            backgroundColor: 'rgba(121, 113, 234, 0.1)',
            borderColor: 'rgb(121, 113, 234)',
            data: Object.values(this.analytics.messages.conversations),
          },
        ],
      };

      this.firstRepliesChartData = {
        labels: ['0-1 hrs', '1-8 hrs', '8-24 hrs', '>24 hrs'],
        datasets: [
          {
            label: 'Percentage',
            backgroundColor: 'rgba(61, 139, 252, 0.45)',
            borderColor: 'rgb(61, 139, 252)',
            data: Object.values(this.analytics.message_responses.total_first_replies),
          },
        ],
      };

      this.notificationsChartData = {
        labels: this.chartLabels,
        datasets: [
          {
            label: 'All Sent',
            backgroundColor: 'rgba(61, 139, 252, 0.1)',
            borderColor: 'rgb(61, 139, 252)',
            data: Object.values(this.analytics.sent_template_messages.sent),
          },
          {
            label: 'All Failed',
            backgroundColor: 'rgb(237, 116, 116, 0.1)',
            borderColor: 'rgb(237, 116, 116)',
            data: Object.values(this.analytics.sent_template_messages.failed),
          },
        ],
      };

      this.automationChartData = {
        labels: this.chartLabels,
        datasets: [
          {
            label: 'All Interactions',
            backgroundColor: 'rgba(61, 139, 252, 0.1)',
            borderColor: 'rgb(61, 139, 252)',
            data: Object.values(this.analytics.automation.all_interactions),
          },
          {
            label: 'Unique Interactions',
            backgroundColor: 'rgba(121, 113, 234, 0.1)',
            borderColor: 'rgb(121, 113, 234)',
            data: Object.values(this.analytics.automation.unique_interactions),
          },
        ],
      };

      // this.paymentsChartData = {
      //   labels: this.chartLabels,
      //   datasets: [
      //     {
      //       label: 'item 1',
      //       backgroundColor: 'rgba(121, 113, 234, 0.35)',
      //       borderColor: 'rgb(121, 113, 234)',
      //       data: [1200, 1200, 3925, 1200, 1200, 1000, 1000, 1000, 1000, 1000, 1000, 1000],
      //     },
      //     {
      //       label: 'item 2',
      //       backgroundColor: 'rgba(61, 139, 252, 0.35)',
      //       borderColor: 'rgb(61, 139, 252)',
      //       data: [1000, 1000, 1000, 1000, 1000, 3611, 5137, 4900, 4500, 3611, 1500, 1300],
      //     },
      //   ],
      // };
    },
    durationToString(seconds) {
      // eslint-disable-next-line no-underscore-dangle
      const durations = duration(seconds, 'seconds')._data;

      const dayDisplay = durations.days > 0 ? `${durations.days}d ` : '';
      const hourDisplay = durations.hours > 0 ? `${durations.hours}h ` : '';
      const minuteDisplay = durations.minutes > 0 ? `${durations.minutes}m` : '';

      return `${dayDisplay}${hourDisplay}${minuteDisplay}`;
    },
  },
};
</script>
